import React from 'react';
import { Grid, Stack } from '@mui/material';
import { breakpoints, Heading, Text, colors, Container } from '@web-for-marketing/react-ui';
import { getTranslation } from '@helpers/languageTranslations';
import { useTranslation } from '@stateManagement/TranslationContext';
import { Picture } from '@components/Picture';
import { useAtom } from 'jotai';
import { selectedLanguageAtom } from '@atoms/appSettings';
import { getFormattedLocalDateTime } from '@helpers/dates';
import { PublicDateTime } from '@components/PublicDateTime';
import { ClassNames } from '@emotion/react';
import { PressRelease } from '@models/pressRelease';

interface MediaReleaseHeaderProps {
    mediaRelease: PressRelease;
}

const classes = {
    headerContainer: {
        padding: '5.6rem 6.4rem',
        borderRadius: '.8rem .8rem 0 0',
        backgroundColor: colors.background.white,
        [`@media (max-width: ${breakpoints.lg})`]: {
            padding: '5.6rem 0',
        },
        [`@media (max-width: ${breakpoints.md})`]: {
            padding: '2.4rem 0',
        },
    },
    contentContainer: {
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
    },
    titleContainer: {
        margin: '1.6rem  0',
    },
    picture: {
        display: 'block',
    },
    bannerImage: {
        borderRadius: '.8rem',
    },
    gridContainer: {
        boxSizing: 'border-box',
        backgroundColor: colors.background.light,
        [`@media (max-width: ${breakpoints.lg})`]: {
            backgroundColor: colors.background.white,
        },
    },
    title: {
        marginBottom: '1rem',
    },
    flexContainer: {
        display: 'flex',
    },
    ellipseIcon: {
        margin: ' 0 1.2rem',
        display: 'flex',
        alignItems: 'center',
        color: colors.text.tertiary,
        fontSize: '2rem',
        [`@media (max-width: ${breakpoints.sm})`]: {
            margin: '0 .8rem',
        },
    },
    media: {
        height: '5rem',
        width: '5rem',
        borderRadius: '50%',
        marginRight: '1.6rem',
    },
} as const;

export const MediaReleaseHeader = ({ mediaRelease }: MediaReleaseHeaderProps): React.ReactElement => {
    const { selectedLanguageTranslation } = useTranslation();
    const [selectedLanguage] = useAtom(selectedLanguageAtom);

    const { title, description, modifiedDateUtc, readMinutes, imageAlt, imageUrl, mobileImageUrl, mobileHeaderAlt } =
        mediaRelease;
    const readMinuteText = getTranslation(selectedLanguageTranslation, 'blogInfo', 'readMinutes').replace(
        '5',
        readMinutes ? readMinutes : '~1'
    );

    return (
        <Container css={classes.gridContainer}>
            <Grid container spacing={{ xs: 2, md: 4, lg: 7 }} css={classes.headerContainer}>
                <Grid size={{ xs: 12, md: 6 }} css={classes.contentContainer}>
                    <div css={classes.titleContainer}>
                        <Heading variant='h2' component='h1' css={classes.title}>
                            {title}
                        </Heading>
                        {description ? (
                            <Text variant='h4' weight='medium' color='secondary'>
                                {description}
                            </Text>
                        ) : null}
                    </div>
                    <div css={classes.flexContainer}>
                        <Stack spacing='4px'>
                            <Stack direction='row' alignItems='center'>
                                <PublicDateTime>
                                    <Text variant='body2' color='tertiary'>
                                        {getFormattedLocalDateTime(modifiedDateUtc, 'LL', selectedLanguage)}
                                    </Text>
                                </PublicDateTime>
                                <span css={classes.ellipseIcon}>&#8226;</span>
                                <Text variant='body2' color='tertiary'>
                                    {readMinuteText}
                                </Text>
                            </Stack>
                        </Stack>
                    </div>
                </Grid>
                {imageUrl || mobileImageUrl ? (
                    <Grid size={{ xs: 12, md: 6 }}>
                        <ClassNames>
                            {({ css }) => (
                                <Picture
                                    lazyLoad={false}
                                    imagePath={imageUrl}
                                    mobilePath={mobileImageUrl}
                                    mobileAlt={mobileHeaderAlt || ''}
                                    alt={imageAlt || ''}
                                    css={classes.bannerImage}
                                    pictureProps={{ className: css(classes.picture) }}
                                    aspectRatio='16/9'
                                />
                            )}
                        </ClassNames>
                    </Grid>
                ) : null}
            </Grid>
        </Container>
    );
};
